import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateResourceQueue } from '../../api/production-request-api/index.js';
import { toast } from '@pella/sash-framework';
import { QUERY_KEYS } from '../../data/constants';
import { useAppContext } from '../../providers';

/**
 * Hook containing mutation function for updating a Resource Queue.
 */
export const useUpdateResourceQueue = () => {
  const queryClient = useQueryClient();
  const { selectedPlant } = useAppContext();
  const resourcesKey = [QUERY_KEYS.RESOURCE_DATA, selectedPlant, { resourceQueues: true }];
  const queuesKey = [QUERY_KEYS.QUEUE_DATA, selectedPlant, { resourceQueues: true }];

  return useMutation({
    /**
     * mutateFn - Function to call the updateResourceQueue api.
     * @param {object} params - Parameters for updating a resource queue.
     * @param {Pella.ProductionRequestAPI.Queue['id']} params.queueId - ID of the queue to assign.
     * @param {Pella.ProductionRequestAPI.Resource['id']} params.resourceId - ID of the resource.
     * @param {Pella.ProductionRequestAPI.ResourceQueueByStatus['queueStatus']} [params.queueStatus] - Status of the queue.
     * @param {boolean} [params.active] - Whether the queue is active.
     */
    mutationFn: ({ queueId, resourceId, queueStatus, active }) =>
      updateResourceQueue(queueId, resourceId, { queueStatus, active }),
    onMutate: async ({ queueId, resourceId, active }, context) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await context.client.cancelQueries({ queryKey: resourcesKey });
      await context.client.cancelQueries({ queryKey: queuesKey });

      // Snapshot the previous value for both resources and queues
      const previousResources = context.client.getQueryData(resourcesKey);
      const previousQueues = context.client.getQueryData(queuesKey);

      // Optimistically update the resource data in the cache
      context.client.setQueryData(
        resourcesKey,
        (/** @type {Pella.ProductionRequestAPI.ResourcesWithAssignments[]} */ oldData) => {
          // Guard against undefined data - if no data in cache, return empty array or previous data
          if (!oldData || !Array.isArray(oldData)) {
            return previousResources || [];
          }
          return oldData.map((resource) =>
            resource.id === resourceId
              ? {
                  ...resource,
                  resourceQueues: resource?.resourceQueues?.map((rq) =>
                    rq.queueId === queueId ? { ...rq, active } : rq,
                  ),
                }
              : resource,
          );
        },
      );

      // Optimistically update the queue data in the cache
      context.client.setQueryData(
        queuesKey,
        (/** @type {Pella.ProductionRequestAPI.QueuesWithAssignments[]} */ oldData) => {
          // Guard against undefined data - if no data in cache, return empty array or previous data
          if (!oldData || !Array.isArray(oldData)) {
            return previousQueues || [];
          }
          return oldData.map((queue) =>
            queue.id === queueId
              ? {
                  ...queue,
                  resourceQueues: queue?.resourceQueues?.map((rq) =>
                    rq.resourceId === resourceId ? { ...rq, active } : rq,
                  ),
                }
              : queue,
          );
        },
      );

      // Return a result with the snapshotted value
      return { previousResources, previousQueues };
    },
    onError: (_err, _variables, onMutateResult, context) => {
      // Rollback on error
      context.client.setQueryData(resourcesKey, onMutateResult?.previousResources);
      context.client.setQueryData(queuesKey, onMutateResult?.previousQueues);
    },
    onSuccess: (_data, { resourceId }) => {
      toast.dismiss();
      toast.success('Assignment updated successfully!');

      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RESOURCE_QUEUES, resourceId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RESOURCE_DATA] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUEUE_DATA] });
    },
  });
};
